import { StarOrnament, StarPattern } from "@/components/Ornament";
import { SITE } from "@/config/site";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import { BookButton } from "@/components/GoldButton";
import { AskChittiButton } from "@/components/AskChittiButton";
import "@/styles/theme-v2.css";

// ============================================================================
// TICKETS V2 — same information architecture as the live Tickets.jsx, same
// conditional logic (the tier grid only renders if SITE.ticketCategories is
// ever populated — it is deliberately empty today; nothing here fabricates
// tiers that haven't been officially announced). What's elevated is the
// visual weight given to the one thing that matters most on this page: the
// price and the "book now" moment.
// ============================================================================

export function TicketsV2() {
  const facts = [
    `Fully seated auditorium — ${SITE.venue.capacity.toLowerCase()}`,
    SITE.seatingNote,
    `Age limit: ${SITE.ageLimit}`,
    SITE.parking,
    `Languages: ${SITE.languages.join(", ")}`,
  ];

  return (
    <section id="tickets" data-testid="tickets-section" className="relative overflow-hidden px-5 py-20 sm:px-8 sm:py-28 lg:py-36">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-24 bottom-0 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/[0.06] blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-6xl">
        <ChapterHeading number="06" title="Tickets" urdu="ٹکٹ" testId="tickets-heading" />

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <Reveal className="h-full lg:col-span-3">
            <div
              data-testid="tickets-live-card"
              className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-[#D4AF37]/45 bg-[#161310] p-8 sm:p-12"
            >
              {/* new: a rare, deliberate crimson glow in the top corner — the
                  brand's documented accent color, finally put to work behind
                  the single most important number on the page */}
              <div className="cah-crimson-burst pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full opacity-60 blur-3xl" aria-hidden="true" />
              <div
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_0%,rgba(212,175,55,0.14),transparent_60%)]"
                aria-hidden="true"
              />
              <p className="relative text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
                Official Ticketing · BookMyShow
              </p>
              <p
                data-testid="tickets-price-from"
                className="cah-shimmer-text font-display relative mt-5 text-5xl leading-none sm:text-6xl"
              >
                ₹{SITE.priceFrom} onwards
              </p>
              <div className="relative mt-6 flex flex-wrap gap-3">
                <span
                  data-testid="tickets-status-filling-fast"
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.08] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#F3E5AB] sm:text-sm"
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#E6CA65]" aria-hidden="true" />
                  {SITE.listingStatus}
                </span>
                <span
                  data-testid="tickets-status-interested"
                  className="inline-flex items-center rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.08] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#F3E5AB] sm:text-sm"
                >
                  {SITE.interestedCount} interested
                </span>
                {/* new: a small emerald "verified" marker — the brand's other
                    documented, never-used accent color, applied once, sparingly,
                    as a trust cue rather than decoration */}
                <span className="cah-emerald-badge inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#EAF4EF] sm:text-sm">
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  Official Partner
                </span>
              </div>
              <p className="relative mt-6 text-sm leading-[1.8] text-[#918A7E] sm:text-base">
                {SITE.seatingNote}
              </p>
              <div className="relative mt-8">
                <BookButton
                  location="tickets"
                  label="Tickets Are Live"
                  testId="tickets-book-btn"
                  className="w-full sm:w-auto"
                />
                <p className="mt-4 text-sm text-[#918A7E]">
                  You will complete your purchase securely on BookMyShow. This website does not sell
                  tickets directly.
                </p>
                <div className="relative mt-6 flex flex-wrap items-center gap-3 border-t border-[#D4AF37]/10 pt-6">
                  <AskChittiButton
                    location="tickets_section"
                    label="Not Sure? Ask Chitti AI"
                    testId="tickets-ask-chitti-btn"
                  />
                  <p className="text-xs leading-relaxed text-[#918A7E] sm:text-sm">
                    Instant answers on WhatsApp — pricing, seating &amp; timings.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="h-full lg:col-span-2">
            <div
              data-testid="tickets-facts-card"
              className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-[#D4AF37]/20 bg-[#0E0C0A] p-8 sm:p-10"
            >
              {/* new: the same understated star-pattern + gold glow texture used
                  behind photo-ready placeholders elsewhere on the site, so this
                  card doesn't read as flatter than the one beside it */}
              <StarPattern id="tickets-facts-pattern" opacity={0.14} className="opacity-60" />
              <div
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_100%_0%,rgba(212,175,55,0.1),transparent_60%)]"
                aria-hidden="true"
              />
              <h3 className="relative font-display text-2xl text-[#F7F4EF]">Good to know</h3>
              <ul className="relative mt-6 flex-1 space-y-5">
                {facts.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm leading-relaxed text-[#C8C2B7] sm:text-base">
                    <StarOrnament className="mt-1 h-3.5 w-3.5 shrink-0 text-[#D4AF37]" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {SITE.ticketCategories.length > 0 && (
          <div className="mt-14">
            <Reveal>
              <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
                Choose Your Category
              </p>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {SITE.ticketCategories.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.06} className="h-full">
                  <div
                    data-testid={`ticket-card-${t.id}`}
                    className={`relative flex h-full flex-col overflow-hidden rounded-3xl border bg-[#161310] ${
                      t.topTier ? "border-[#D4AF37]/60" : "border-[#D4AF37]/25"
                    }`}
                  >
                    {t.topTier && (
                      <span
                        data-testid={`ticket-top-tier-badge-${t.id}`}
                        className="absolute right-3 top-3 z-10 rounded-full border border-[#D4AF37]/50 bg-[#0A0805]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#E6CA65] backdrop-blur-sm"
                      >
                        Top Tier
                      </span>
                    )}
                    {/* same rich crimson header treatment used across the site's
                        premium surfaces — applied uniformly, the top-tier badge
                        above does the visual distinguishing, not a new color */}
                    <div className="cah-crimson-burst flex h-14 items-center justify-center border-b border-[#D4AF37]/25">
                      <StarOrnament className="h-5 w-5 text-[#E6CA65]" />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-display text-xl text-[#F7F4EF]">{t.name}</h3>
                      <p
                        data-testid={`ticket-price-${t.id}`}
                        className="font-display mt-3 text-2xl text-[#E6CA65]"
                      >
                        {t.price ? `₹${t.price.toLocaleString("en-IN")}` : "Price TBA"}
                      </p>
                      {t.description && (
                        <p className="mt-3 flex-1 text-sm leading-relaxed text-[#918A7E]">{t.description}</p>
                      )}
                      <BookButton
                        location="tickets"
                        size="sm"
                        testId={`ticket-book-btn-${t.id}`}
                        className="mt-6 w-full"
                      />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
